// Minimal reactive primitives. ~60 lines, no dependencies.
let currentEffect = null;
const effectStack = [];
let batchDepth = 0;
const pendingEffects = new Set();

/** A reactive value. Call with no args to read, one arg to write. */
export function signal(initialValue) {
  let value = initialValue;
  const subscribers = new Set();
  function accessor(...args) {
    if (args.length === 0) {
      if (currentEffect) {
        subscribers.add(currentEffect);
        currentEffect.deps.add(subscribers); // track for cleanup so stale deps don't pile up
      }
      return value;
    }
    const next = args[0];
    if (Object.is(next, value)) return value;
    value = next;
    for (const sub of [...subscribers]) {
      if (batchDepth > 0) pendingEffects.add(sub);
      else sub.run();
    }
    return value;
  }
  accessor.peek = () => value;
  accessor.update = (fn) => accessor(fn(value));
  return accessor;
}

// Drop a runner from every signal it had subscribed to (called before each re-run and on
// dispose) so an effect only ever stays subscribed to the signals it read on its LAST run —
// conditional dependencies don't accumulate, and a disposed effect fully unsubscribes.
function cleanupDeps(runner) {
  for (const dep of runner.deps) dep.delete(runner);
  runner.deps.clear();
}

/** Re-runs `fn` whenever any signal it reads changes. Returns a runner with `.dispose()`. */
export function effect(fn) {
  const runner = {
    deps: new Set(),
    run() {
      cleanupDeps(runner);
      effectStack.push(currentEffect);
      currentEffect = runner;
      try {
        fn();
      } finally {
        currentEffect = effectStack.pop() ?? null;
      }
    },
    dispose() {
      cleanupDeps(runner);
    },
  };
  runner.run();
  return runner;
}

/**
 * Run `fn` without subscribing the current effect to any signals it reads. Use when a
 * reactive context needs to *read* state without re-running when that state changes — e.g.
 * building a manually-rendered screen inside an effect that should only track the route.
 */
export function untrack(fn) {
  const prev = currentEffect;
  currentEffect = null;
  try {
    return fn();
  } finally {
    currentEffect = prev;
  }
}

export function computed(fn) {
  const s = signal(undefined);
  effect(() => s(fn()));
  return s;
}

/** Coalesce multiple signal writes into a single effect flush. */
export function batch(fn) {
  batchDepth++;
  try {
    fn();
  } finally {
    batchDepth--;
    if (batchDepth === 0) {
      const effects = [...pendingEffects];
      pendingEffects.clear();
      for (const e of effects) e.run();
    }
  }
}
